import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CourtBoardResponse } from "@bw/shared/types/generated";
import { courtBoardQueryOptions } from "./courtBoardQuery";
import { useDropInCourtState } from "./useDropInCourtState";

const MAX_TIMEOUT_MS = 2_147_483_647;

function retryIsAllowed(
  retry: unknown,
  failureCount: number,
  error: Error,
): boolean {
  if (retry === true) return true;
  if (retry === false) return false;
  if (typeof retry === "number") return failureCount < retry;
  if (typeof retry === "function") {
    return Boolean(
      (retry as (count: number, queryError: Error) => boolean)(
        failureCount,
        error,
      ),
    );
  }
  return failureCount < 3;
}

export interface UseCourtBoardOptions {
  enabled?: boolean;
  authorizationExpiresAt?: string | null;
}

/**
 * The self-serve court board for a facility (GET /facilities/{id}/drop-in/court-board
 * → CourtBoardResponse). Lists every non-deleted court with independent
 * operational, occupancy, and viewer-specific availability states, live
 * capacity/privacy facts, current players, and the looking_to_play roster.
 *
 * The backend is authoritative on availability; the FE never derives action
 * eligibility from occupancy, capacity, or privacy. Court mutations invalidate
 * this key so the board re-reads the server's truth.
 */
export function useCourtBoard(
  facilityId: string,
  options: UseCourtBoardOptions = {},
) {
  const queryClient = useQueryClient();
  const requestedEnabled = Boolean(facilityId) && (options.enabled ?? true);
  const deadlineConfigured = options.authorizationExpiresAt !== undefined;
  const deadlineMs =
    typeof options.authorizationExpiresAt === "string"
      ? Date.parse(options.authorizationExpiresAt)
      : Number.NaN;
  const [authorizationSchedule, setAuthorizationSchedule] = useState(0);
  const authorizationRef = useRef({
    requestedEnabled,
    deadlineConfigured,
    deadlineMs,
  });
  authorizationRef.current = {
    requestedEnabled,
    deadlineConfigured,
    deadlineMs,
  };
  const isAuthorizedNow = useCallback(() => {
    const authorization = authorizationRef.current;
    if (!authorization.requestedEnabled) return false;
    if (!authorization.deadlineConfigured) return true;
    return (
      Number.isFinite(authorization.deadlineMs) &&
      Date.now() < authorization.deadlineMs
    );
  }, []);
  const authorized = isAuthorizedNow();
  const queryOptions = courtBoardQueryOptions(queryClient, facilityId);
  const configuredRetry = queryClient.defaultQueryOptions(queryOptions).retry;

  useEffect(() => {
    if (
      !requestedEnabled ||
      !deadlineConfigured ||
      !Number.isFinite(deadlineMs) ||
      deadlineMs <= Date.now()
    ) {
      return;
    }
    const timer = window.setTimeout(
      () => setAuthorizationSchedule((value) => value + 1),
      Math.min(deadlineMs - Date.now(), MAX_TIMEOUT_MS),
    );
    return () => window.clearTimeout(timer);
  }, [authorizationSchedule, deadlineConfigured, deadlineMs, requestedEnabled]);

  const query = useQuery<CourtBoardResponse>({
    ...queryOptions,
    queryFn: async () => {
      if (!isAuthorizedNow()) {
        throw new Error("Court board authorization expired");
      }
      const board = await queryOptions.queryFn();
      if (!isAuthorizedNow()) {
        throw new Error("Court board authorization expired");
      }
      return board;
    },
    enabled: () => isAuthorizedNow(),
    retry: (failureCount, error) =>
      isAuthorizedNow() && retryIsAllowed(configuredRetry, failureCount, error),
    refetchOnWindowFocus: () => (isAuthorizedNow() ? "always" : false),
    refetchOnReconnect: () => (isAuthorizedNow() ? "always" : false),
  });
  const { isActorRevisionPending, isBoardRevisionPending } =
    useDropInCourtState(facilityId, query.data?.revision ?? 0, {
      enabled: authorized,
      authorizationExpiresAt: options.authorizationExpiresAt,
    });
  const isRevisionPending = isActorRevisionPending || isBoardRevisionPending;

  return {
    ...query,
    data: authorized ? query.data : undefined,
    isRevisionPending: authorized && isRevisionPending,
  };
}
